<template>
  <div>
    <div class="flex flex-col w-full relative">
      <b-loading :is-full-page="false" :active.sync="isLoading" :can-cancel="false"></b-loading>
      <div v-if="pictogram == null">
        <ProgressOverview
          :pictograms="pictograms"
          :company-alias="company.alias"
          :img-plant="imgPlant"
          :is-loading-image="isLoadingImage"
          :has-viewer="!!(powerPlant && powerPlant.id)"
          @image-load="onImageLoad"
          @image-error="onImageError"
          @show-image-plant="_ShowImagePlant"
          @select-pictogram="_HandleClickSelectPictogram"
          @force-refresh="_HandleForceRefresh"
          @open-viewer="_HandleOpenViewer"
          @go-back="$emit('go-back')"
        />
      </div>
      <div v-else>
        <ProgressDetail
          :pictogram="pictogram"
          :company-alias="company.alias"
          :img-pictogram="imgPictogram"
          :is-loading-image="isLoadingImage"
          :ratios="ratios"
          :validation-times="validationTimes"
          @image-load="onImageLoad"
          @image-error="onImageError"
          @show-image="_ShowImage"
          @go-back="_HandleGoBack"
        />
      </div>
    </div>

    <!-- Visor del parque en modal full-screen (abierto desde "avance proyecto"). Se destruye
         al cerrar (destroy-on-hide) para liberar el visor (Konva/Uppy). Sin cancelar por Esc
         (el visor usa Esc para su propio foco/herramientas); se cierra con el botón. -->
    <b-modal :active.sync="showViewerModal" full-screen :destroy-on-hide="true"
      :can-cancel="false" aria-role="dialog">
      <div class="epc-viewer-modal">
        <div class="epc-viewer-modal-bar">
          <span class="epc-viewer-modal-title">{{ $t('photovoltaic_park') }}</span>
          <button class="epc-viewer-modal-close" type="button" :aria-label="$t('close')"
            @click="showViewerModal = false">
            <b-icon icon="xmark" pack="fas"></b-icon>
          </button>
        </div>
        <div class="epc-viewer-modal-body">
          <ViewerPageDesign v-if="powerPlant && powerPlant.id"
            :entity-id="powerPlant.id"
            :entity-type="'solar-plant'"
            :binary-only="true"
            :can-actions-pictogram="viewerCanActions" />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import EventBus from "~/plugins/bus";
import ProgressOverview from "./dashboard/ProgressOverview.vue";
import ProgressDetail from "./dashboard/ProgressDetail.vue";
import ViewerPageDesign from "./solar-park/views/ViewerPageDesign.vue";

export default {
  name: "manage-photovoltaic-park",

  components: {
    ProgressOverview,
    ProgressDetail,
    ViewerPageDesign,
  },

  data: () => ({
    pictograms: [],
    pictogram: null,
    isLoading: false,
    isLoadingImage: false,

    imgPictogram: {
      src: null,
      extension: null,
      type: null
    },

    powerPlant: null,
    imgPlant: {
      src: null,
      extension: null,
      type: null,
      updatedAt: null,
    },

    ratios: {},
    validationTimes: [],

    // Visor del parque en modal full-screen (abierto desde "avance proyecto").
    showViewerModal: false,
    // canActions de USUARIO (distinto del bot): sin guardar captura al parque, sin modo
    // diseño/insert/back/LOD; interacciones y selector de vistas activos (por defecto).
    viewerCanActions: {
      show_header: false,
      show_areas: true,
      show_screenshot: true,
      show_save_screenshot: false,
      save: false,
      show_design_mode: false,
      show_insert: false,
      show_back: false,
      show_lod: false,
    },
  }),

  props: {
    projectId: { type: Number, required: true },
    showDownloadFileOriginal: { type: Boolean, default: true },
    permittedActions: { type:Object, default() { return {} } },
    canActionsPictogram: { type:Object, default() { return {} } }
  },

  computed: {
    ...mapGetters({
      company: "app/company",
    }),
  },

  watch: {
    pictogram(newVal) {
      if (newVal !== null && this.company.alias === 'solaring') {
        this.getRatioPictograms();
        this.getValidationTimes();
      }
    }
  },

  created() {
    EventBus.$on('refreshPictogramScreenshot', this._OnScreenshotUpdated);
    this.getProjectPictograms();
  },

  beforeDestroy() {
    EventBus.$off('refreshPictogramScreenshot', this._OnScreenshotUpdated);
  },

  methods: {
    _OnScreenshotUpdated(e) {
      if (e.entity === 'power-plant' && this.powerPlant?.id === e.entity_id) {
        // refrescar imgPlant.updatedAt + cache-bust si layer activo coincide
        // Sincronizar metadata local
        if (!this.powerPlant.metadata) this.powerPlant.metadata = {}
        if (!this.powerPlant.metadata.screenshots) this.powerPlant.metadata.screenshots = {}
        this.powerPlant.metadata.screenshots[e.layer] = { last_update: e.last_update }

        // Solo refrescar si la capa activa coincide
        if (this.imgPlant.type === e.layer && this.imgPlant.src) {
          this.imgPlant.updatedAt = e.last_update
          this.isLoadingImage = true
          const url = new URL(this.imgPlant.src)
          url.searchParams.set('v', Date.now())
          this.imgPlant.src = url.href
        }
        // this._HandleForceRefresh();
      } else if (e.entity === 'pictogram' && this.pictogram?.id === e.entity_id) {
        // mismo patrón con imgPictogram
      }
    },

    async getRatioPictograms() {
      if (!this.pictogram) {
        return;
      }

      try {
        this.isLoading = true;
        let params = {
          with_ratios: 1
        };

        const { data } = await axios.get(
          `/api/v2/pictogram/${this.pictogram.id}/ratios`,
          { params: params }
        );
        if (data && data.success) {
          if (data.ratios) {
            this.ratios = data.ratios
          }
        } else {
          this.$notify.error("error_loading_documents");
        }
      } catch (error) {
        this.$notify.error("error_loading_documents");
      } finally {
        this.isLoading = false;
      }
    },

    async getValidationTimes() {
      if (!this.pictogram) {
        return;
      }

      try {
        this.isLoading = true;

        const { data } = await axios.get(
          `/api/v2/pictogram/${this.pictogram.id}/validation-times`
        );

        if (data && data.success) {
          if (data.validation_times) {
            this.validationTimes = data.validation_times;
          }
        } else {
          this.$notify.error("error_loading_data");
        }
      } catch (error) {
        this.$notify.error("error_loading_data");
      } finally {
        this.isLoading = false;
      }
    },

    async getProjectPictograms() {
      this.isLoading = true;
      let params = {
        with_screenshot: 1,
        with_associated_activity: 1,
        with_power_plant: 1
      };

      const { data } = await axios.get(
        "/api/v2/project/" + this.projectId + "/pictograms",
        { params: params }
      );

      if (data && data.success) {
        this.pictograms = data.pictograms.filter((el) => {
          return el.activity_planning_code && ! el.activity_planning_code.includes('.R.')
        });

        this.powerPlant = data.powerPlant

        if(data.powerPlant) {
          this.isLoadingImage = true

          // TODO: aqui falta condision en caso de que screenshot sea null
          this.imgPlant.src = data.powerPlant.screenshot + '?v=' + new Date().getTime()
          this.imgPlant.extension = data.powerPlant.screenshot.split('.').pop()
          let tmp = data.powerPlant.screenshot.split('_')
          this.imgPlant.type = tmp[tmp.length - 1].split('.').shift()

          // Timestamp de la última actualización del screenshot del layer activo
          const screenshots = (data.powerPlant.metadata && data.powerPlant.metadata.screenshots) || {}
          this.imgPlant.updatedAt = (screenshots[this.imgPlant.type] && screenshots[this.imgPlant.type].last_update) || null
        }

        this.total = data.pictograms ? data.pictograms.length : 0;
      } else {
        this.$notify.error("error_loading_documents");
      }
      this.isLoading = false;
    },

    _ShowImage(type) {
      if (this.pictogram != null && this.pictogram.screenshot != null) {
        this.isLoadingImage = true
        this.imgPictogram.src = this.imgPictogram.src.replace('_' + this.imgPictogram.type + '.' + this.imgPictogram.extension, '_' + type + '.' + this.imgPictogram.extension)

        let url = new URL(this.imgPictogram.src)
        url.searchParams.delete('v')
        this.imgPictogram.src = url.href

        this.imgPictogram.src += '?v=' + new Date().getTime()
        this.imgPictogram.type = type
      }
    },

    _ShowImagePlant(type) {
      if (this.powerPlant != null && this.powerPlant.screenshot != null) {
        this.isLoadingImage = true
        this.imgPlant.src = this.imgPlant.src.replace('_' + this.imgPlant.type + '.' + this.imgPlant.extension, '_' + type + '.' + this.imgPlant.extension)

        let url = new URL(this.imgPlant.src)
        url.searchParams.delete('v')
        this.imgPlant.src = url.href

        this.imgPlant.src += '?v=' + new Date().getTime()
        this.imgPlant.type = type

        // Timestamp del layer al que se cambia
        const screenshots = (this.powerPlant.metadata && this.powerPlant.metadata.screenshots) || {}
        this.imgPlant.updatedAt = (screenshots[type] && screenshots[type].last_update) || null
      }
    },

    async _HandleForceRefresh() {
      // Re-fetch para que tanto la imagen como el timestamp queden al día.
      await this.getProjectPictograms()
    },

    // Abre el visor del parque en un modal full-screen (mismo componente que la ruta del
    // bot, pero con canActions de usuario). Tras abrir, fuerza un resize para que Konva
    // dimensione al tamaño del modal.
    _HandleOpenViewer() {
      if (!this.powerPlant || !this.powerPlant.id) return
      this.showViewerModal = true
      this.$nextTick(() => {
        setTimeout(() => window.dispatchEvent(new Event('resize')), 120)
      })
    },

    _HandleClickSelectPictogram(item) {
      this.isLoadingImage = true
      this.pictogram = item

      if (this.pictogram.screenshot != null) {
        this.imgPictogram.src = item.screenshot + '?v=' + new Date().getTime()
        this.imgPictogram.extension = item.screenshot.split('.').pop()
        let tmp = item.screenshot.split('_')
        this.imgPictogram.type = tmp[tmp.length - 1].split('.').shift()
      }
    },

    _HandleGoBack() {
      this.isLoadingImage = false;
      this.pictogram = null;
      this.imgPictogram.src = null;
      this.imgPictogram.extension = null;
      this.imgPictogram.type = null;
    },

    onImageLoad() {
      this.isLoadingImage = false;
    },
    onImageError() {
      this.isLoadingImage = false;
      console.error('Error al cargar la imagen');
    }
  }

};
</script>

<style scoped>
/* Visor dentro del modal full-screen: barra fina (título + cerrar) + cuerpo con el visor. */
.epc-viewer-modal {
  width: 100vw;
  height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.epc-viewer-modal-bar {
  flex: none;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  border-bottom: 1px solid #eceef1;
}

.epc-viewer-modal-title {
  font-size: 14px;
  font-weight: 700;
  color: #3a6bc8;
}

.epc-viewer-modal-body {
  flex: 1;
  min-height: 0;
  position: relative;
}

.epc-viewer-modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e3e3e8;
  border-radius: 8px;
  background: #ffffff;
  color: #343a40;
  cursor: pointer;
}

.epc-viewer-modal-close:hover {
  background: #f4f4f6;
  color: #3a6bc8;
}
</style>
